import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { CashflowLockup } from "@/components/Logo";
import { useAuth } from "@/auth/AuthContext";
import { TEST_IDS } from "@/testIds";

const DEMO_EMAIL = "admin@radiance.co.in";
const DEMO_PASSWORD = "cashflow2026";
const HERO_IMAGE =
  "https://images.pexels.com/photos/302769/pexels-photo-302769.jpeg?auto=compress&cs=tinysrgb&w=1600";

export default function Login() {
  const { user, checking, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(DEMO_EMAIL);
  const [password, setPassword] = useState(DEMO_PASSWORD);
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!checking && user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Signed in", { description: "Welcome back to Cashflow." });
      navigate("/");
    } else {
      setError(res.error || "Login failed");
    }
  };

  return (
    <div
      className="min-h-screen w-full flex flex-col md:flex-row relative overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse 90% 60% at 100% 0%, rgba(244, 63, 94, 0.14), transparent 60%), radial-gradient(ellipse 70% 60% at 0% 100%, rgba(99, 102, 241, 0.14), transparent 55%), var(--cf-canvas)",
      }}
    >
      {/* LEFT PANEL — glass form */}
      <div className="flex-1 md:flex-[1_1_46%] flex items-center justify-center px-6 md:px-14 py-10 md:py-14 relative z-10">
        <div className="w-full max-w-md">
          <div className="mb-10 md:mb-12">
            <CashflowLockup />
          </div>

          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold uppercase mb-7 cf-pill-accent cf-pill"
            style={{ letterSpacing: "0.14em" }}
          >
            <span className="w-1 h-1 rounded-full bg-coral" />
            Collections Intelligence
          </div>

          <h1
            className="font-display mb-4"
            style={{ fontSize: "clamp(34px, 5vw, 48px)", fontWeight: 800, letterSpacing: "-0.032em", lineHeight: 1.02 }}
          >
            Collections you can <span className="gradient-text">plan around.</span>
          </h1>
          <p className="text-[14.5px] mb-10 text-muted-dark" style={{ lineHeight: 1.55 }}>
            Live monthly projection and reconciliation across every Radiance project.
          </p>

          <form onSubmit={submit} className="space-y-4" autoComplete="off">
            <div>
              <label
                htmlFor="email"
                className="block text-[10.5px] font-bold uppercase mb-2 text-muted-dark"
                style={{ letterSpacing: "0.14em" }}
              >
                Email
              </label>
              <input
                id="email"
                data-testid={TEST_IDS.loginEmail}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="cf-touch w-full px-3.5 py-3 rounded-[10px] focus:outline-none transition-all text-[14px]"
                style={{
                  background: "var(--cf-surface)",
                  color: "var(--cf-ink)",
                  border: "1px solid var(--cf-hairline-strong)",
                  boxShadow: "inset 0 1px 0 var(--cf-inset-highlight)",
                }}
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-[10.5px] font-bold uppercase mb-2 text-muted-dark"
                style={{ letterSpacing: "0.14em" }}
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  data-testid={TEST_IDS.loginPassword}
                  type={showPwd ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="cf-touch w-full px-3.5 py-3 pr-12 rounded-[10px] focus:outline-none transition-all num text-[14px]"
                  style={{
                    background: "var(--cf-surface)",
                    color: "var(--cf-ink)",
                    border: "1px solid var(--cf-hairline-strong)",
                    boxShadow: "inset 0 1px 0 var(--cf-inset-highlight)",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-muted-light hover:text-ink transition-colors"
                  aria-label={showPwd ? "Hide password" : "Show password"}
                  data-testid="login-pwd-toggle"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid={TEST_IDS.loginError} className="text-xs text-red-600">
                {error}
              </div>
            )}

            <button
              data-testid={TEST_IDS.loginSubmit}
              type="submit"
              disabled={submitting}
              className="cf-touch cf-btn-primary w-full py-3.5 text-white text-sm font-bold active:scale-[0.98] transition-transform disabled:opacity-60 inline-flex items-center justify-center gap-2 rounded-[10px]"
            >
              {submitting ? "Signing in…" : "Sign in to Cashflow"}
              {!submitting && <ArrowRight size={15} strokeWidth={2.5} />}
            </button>
          </form>

          <div className="mt-8 text-[11px] text-muted-light flex items-center gap-1.5">
            <span className="cf-pulse" />
            Demo credentials pre-filled · press Enter to continue
          </div>
        </div>
      </div>

      {/* RIGHT PANEL — architectural hero with glass overlay card */}
      <div className="hidden md:flex md:flex-[1_1_54%] relative overflow-hidden">
        {/* Base image */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${HERO_IMAGE})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "saturate(1.05) contrast(1.05)",
          }}
        />
        {/* Softer gradient overlay so the architecture is visible but text remains legible */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(6, 8, 16, 0.42) 0%, rgba(11, 14, 24, 0.72) 100%)",
          }}
        />
        {/* Coral aurora */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: "8%", right: "10%",
            width: 380, height: 380, borderRadius: "50%",
            background: "radial-gradient(circle, rgba(244,63,94,0.45) 0%, transparent 65%)",
            filter: "blur(60px)",
          }}
        />
        {/* Indigo aurora */}
        <div
          className="absolute pointer-events-none"
          style={{
            bottom: "6%", left: "12%",
            width: 320, height: 320, borderRadius: "50%",
            background: "radial-gradient(circle, rgba(99,102,241,0.42) 0%, transparent 65%)",
            filter: "blur(60px)",
          }}
        />

        <div className="relative z-10 flex-1 flex flex-col justify-between p-10 lg:p-14">
          <div className="text-[10px] font-bold uppercase text-white/50" style={{ letterSpacing: "0.28em" }}>
            Radiance Realty · Live Projection
          </div>

          <div className="max-w-lg">
            <h2
              className="font-display text-white"
              style={{ color: "#ffffff", fontWeight: 800, fontSize: "clamp(34px, 4vw, 52px)", letterSpacing: "-0.032em", lineHeight: 1.02 }}
            >
              Every rupee,<br />
              every month,<br />
              <span
                style={{
                  background: "linear-gradient(90deg, #FDA4AF 0%, #F43F5E 50%, #A5B4FC 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                in one place.
              </span>
            </h2>
            <p className="mt-5 text-white/70 text-[14px] max-w-md leading-relaxed">
              Command your treasury with pixel-precise projections and milestone-aware forecasts — all in one command center.
            </p>
          </div>

          <div className="flex items-center justify-between text-[11px] text-white/40">
            <div>Internal · Cashflow v3</div>
            <div className="flex items-center gap-2">
              <span className="cf-pulse" /><span>Live</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
